import { currentUser } from "../lib/auth.js";
import { el } from "../lib/dom.js";
import { ApiError } from "../lib/http.js";
import { icon } from "../lib/icons.js";
import {
  disableShare,
  enableShare,
  regenerateShare,
  shareStatus,
  type ShareMode,
  type ShareState,
  type ShareStatus,
} from "../lib/sharing.js";
import { ACCOUNT_NAME, accountNameForm, type AccountNameForm } from "./accountName.js";
import { actionRow, choiceControl, field, messageOf, type Popup, type Shell } from "./ui.js";

/**
 * The share dialog's Link tab (g73): a view link and an edit link to one of
 * the owner's lists, each on or off by itself.
 *
 * Nothing here is believed before the server says it. Opening reads both
 * modes and creates nothing; every change waits for its answer, and an
 * answer that never came is followed by reading the status again, since the
 * server may have done it anyway. Until then the link is not offered: a
 * replaced or turned-off link shown as working is the one thing this must
 * never say.
 *
 * A link is a secret. Its URL lives in this closure and in one field's
 * `value`, never an attribute, a title, storage or an announcement.
 */

export const LINK_WORDS = {
  access: "Access",
  view: "Can view",
  edit: "Can edit",
  // Whether each mode's link exists, said only once the server has (g75).
  active: "Active",
  notCreated: "Not created",
  explain: {
    view: "Anyone with the link sees this list as it changes, including completed items. They can't change it.",
    edit: "Anyone with the link can see and change this list, including completed items. They can't share or delete it.",
  },
  field: { view: "View link", edit: "Edit link" },
  create: { view: "Create view link", edit: "Create edit link" },
  creating: "Creating link…",
  copy: "Copy link",
  share: "Share",
  manage: "Manage link",
  manageNote: { view: "Replacing or turning off this link stops the one you shared from working. The edit link isn't affected.", edit: "Replacing or turning off this link stops the one you shared from working. The view link isn't affected." },
  replace: "Replace link",
  turnOff: "Turn off link",
  loading: "Checking links…",
  replacing: "Replacing link…",
  turningOff: "Turning off link…",
  checking: "Checking what happened…",
  tryAgain: "Try again",
  checkAgain: "Check again",
  signIn: "Sign in again",
  offline: "Can't reach Trayflux. Links need a connection. You can still share a text copy.",
  failed: "Couldn't check this list's links.",
  signedOut: "Your sign-in has expired. Sign in again to manage links.",
  unsynced: "This list hasn't reached Trayflux yet. Try again once it has synced.",
  gone: "This list has been deleted. It can't be shared.",
  createFailed: "Couldn't create the link.",
  copied: { view: "View link copied.", edit: "Edit link copied." },
  shared: "Passed to your share app.",
  manual: "Automatic copying is unavailable. Copy the selected link manually.",
  shareUnavailable: "Sharing is unavailable. Use Copy link.",
  ready: { view: "View link ready. Copy it or share it.", edit: "Edit link ready. Copy it or share it." },
  replaced: { view: "View link replaced. The old link no longer works.", edit: "Edit link replaced. The old link no longer works." },
  notReplaced: { view: "The view link wasn't replaced. It still works.", edit: "The edit link wasn't replaced. It still works." },
  turnedOff: { view: "View link turned off. Anyone who had it can no longer open the list.", edit: "Edit link turned off. Anyone who had it can no longer open the list." },
  offNewer: { view: "The view link you turned off no longer works. A newer view link is on.", edit: "The edit link you turned off no longer works. A newer edit link is on." },
  copyFailed: "Couldn't copy the link.",
  notOff: { view: "The view link wasn't turned off. It still works.", edit: "The edit link wasn't turned off. It still works." },
  unknown: { view: "Couldn't confirm what happened to the view link. Check again before sharing it.", edit: "Couldn't confirm what happened to the edit link. Check again before sharing it." },
  confirmReplace: {
    title: { view: "Replace the view link?", edit: "Replace the edit link?" },
    message: { view: "The view link you've shared will stop working. Only people you give the new link to can open the list.", edit: "The edit link you've shared will stop working. Only people you give the new link to can change the list." },
    keep: "Keep link",
  },
  confirmOff: {
    title: { view: "Turn off the view link?", edit: "Turn off the edit link?" },
    message: { view: "The view link you've shared will stop working. You can create a new one later.", edit: "The edit link you've shared will stop working. You can create a new one later." },
    keep: "Keep link",
  },
} as const;

type Phase = "idle" | "loading" | "ready" | "offline" | "failed" | "signed-out" | "unsynced" | "gone";

type Held = { url: string; generation: string };
type ModeState =
  | { kind: "inactive" }
  | { kind: "naming" }
  | { kind: "creating" }
  | ({ kind: "active" } & Held)
  | ({ kind: "replacing" | "turning-off" } & Held)
  | { kind: "checking"; after: "replace" | "off"; from: string }
  | { kind: "create-failed"; message: string }
  | { kind: "unknown"; after: "replace" | "off"; from: string };

/** States a status answer may overwrite: nothing is on its way for them. */
const STABLE = new Set<ModeState["kind"]>(["inactive", "active", "create-failed", "unknown"]);

const MODES: readonly ShareMode[] = ["view", "edit"];

export interface LinkPanel {
  node: HTMLElement;
  /** The tab is showing: the first call reads the status. */
  show(): void;
  /** A name is being saved: the popup may not close under it. */
  busy(): boolean;
  dispose(): void;
}

export interface LinkPanelOptions {
  shell: Shell;
  listId: string;
  popup(): Popup;
  /** The dialog's Close, which ends this panel's last row. */
  close(): void;
}

function validState(value: unknown): value is ShareState {
  if (typeof value !== "object" || value === null) return false;
  const state = value as Record<string, unknown>;
  if (state.active === false) return true;
  return state.active === true && typeof state.url === "string" && typeof state.generation === "string";
}

function validStatus(value: unknown): value is ShareStatus {
  if (typeof value !== "object" || value === null) return false;
  const status = value as Record<string, unknown>;
  return validState(status.view) && validState(status.edit);
}

const named = (name: string | null | undefined): boolean => typeof name === "string" && name.length > 0;

export function linkPanel({ shell, listId, popup, close: onClose }: LinkPanelOptions): LinkPanel {
  const { store } = shell;
  let alive = true;
  let phase: Phase = "idle";
  let loadSerial = 0;
  const states: Record<ShareMode, ModeState> = { view: { kind: "inactive" }, edit: { kind: "inactive" } };
  const serials: Record<ShareMode, number> = { view: 0, edit: 0 };
  let manageOpen = false;
  let useShare = typeof navigator.share === "function";
  let naming: AccountNameForm | undefined;
  let copying = false;

  // ---- the access choice ---------------------------------------------------

  const markers: Record<ShareMode, HTMLElement> = {
    view: el("span", { class: "share-mode-state" }),
    edit: el("span", { class: "share-mode-state" }),
  };
  // Every opening starts on the lesser of the two: an edit link is never
  // copied out of habit.
  const access = choiceControl<ShareMode>(LINK_WORDS.access, [
    { value: "view", label: LINK_WORDS.view, detail: markers.view },
    { value: "edit", label: LINK_WORDS.edit, detail: markers.edit },
  ], "view");
  access.node.classList.add("share-mode");
  access.node.setAttribute("aria-describedby", "share-link-explain");
  access.node.addEventListener("change", () => {
    manageOpen = false;
    render();
  });
  const explain = el("p", { class: "popup-note", attrs: { id: "share-link-explain" } });
  const body = el("div", { class: "share-link-body" });
  const status = el("p", { class: "share-link-status popup-note" });
  const close = el("button", {
    class: "secondary share-close",
    text: "Close",
    attrs: { type: "button", "data-key": "close" },
    on: { click: onClose },
  });
  // The choice and what it grants are one group; the chosen mode's state,
  // its status and its row follow, a group gap apart (g75).
  const node = el("div", { class: "share-link", children: [el("div", { class: "form-group", children: [access.node, explain] }), body] });

  const mode = (): ShareMode => access.value();
  const other = (of: ShareMode): ShareMode => (of === "view" ? "edit" : "view");

  const say = (message: string) => {
    status.textContent = message;
    if (alive) shell.announce(message);
  };

  /** Lets `List.sharing` and the live refresh learn what changed: only a sync brings them. */
  const changed = () => { void store.sync().catch(() => undefined); };

  // ---- drawing -------------------------------------------------------------

  const button = (key: string, text: string, onClick: () => void, className = "secondary") =>
    el("button", { class: className, text, attrs: { type: "button", "data-key": key }, on: { click: onClick } });

  /** A control that is up but can't act yet: still focusable and named, as the design system says. */
  const inert = (control: HTMLButtonElement) => {
    control.setAttribute("aria-disabled", "true");
    return control;
  };

  let urlField: HTMLTextAreaElement | undefined;

  /**
   * Redraws the chosen mode. When the focus was inside, it goes to `focus`,
   * or to the control with the same key, or to the first control: never to
   * the page underneath.
   */
  function render(focus?: string): void {
    if (!alive) return;
    // The focus moves only when it was in here: an answer arriving while
    // the person is on Close or the tabs leaves them there.
    const inside = document.activeElement instanceof HTMLElement && body.contains(document.activeElement);
    const focused = inside ? (document.activeElement as HTMLElement).dataset.key : undefined;
    const wanted = focus ?? focused;
    const which = mode();
    explain.textContent = LINK_WORDS.explain[which];
    for (const each of MODES) {
      const state = states[each];
      markers[each].textContent = phase !== "ready" ? "" : state.kind === "active" ? LINK_WORDS.active : state.kind === "inactive" ? LINK_WORDS.notCreated : "";
    }
    urlField = undefined;
    const blocks = content(which);
    const state = blocks.slice(0, -1);
    body.replaceChildren(...(state.length ? [el("div", { class: "form-group share-link-state", children: state })] : []), status, ...blocks.slice(-1));
    node.setAttribute("aria-busy", String(phase === "loading"));
    if (inside) {
      const target = (wanted ? body.querySelector<HTMLElement>(`[data-key="${wanted}"]`) : null)
        ?? body.querySelector<HTMLElement>("button, textarea, summary");
      (target ?? access.node.querySelector<HTMLInputElement>("input:checked"))?.focus({ preventScroll: true });
    }
  }

  function note(text: string): HTMLElement {
    return el("p", { class: "popup-note", text });
  }

  function content(which: ShareMode): HTMLElement[] {
    switch (phase) {
      case "idle":
      case "loading":
        return [note(LINK_WORDS.loading), footer()];
      case "offline":
        return [note(LINK_WORDS.offline), footer(undefined, button("retry", LINK_WORDS.tryAgain, load))];
      case "failed":
        return [note(LINK_WORDS.failed), footer(undefined, button("retry", LINK_WORDS.tryAgain, load))];
      case "unsynced":
        return [note(LINK_WORDS.unsynced), footer(undefined, button("retry", LINK_WORDS.tryAgain, load))];
      case "signed-out":
        return [note(LINK_WORDS.signedOut), footer(undefined, button("sign-in", LINK_WORDS.signIn, () => shell.reauth()))];
      case "gone":
        return [note(LINK_WORDS.gone), footer()];
      case "ready":
        break;
    }
    const state = states[which];
    switch (state.kind) {
      // The marker, the explanation and the button say there is none.
      case "inactive":
        return [footer(button("create", LINK_WORDS.create[which], () => create(which), ""))];
      case "naming":
      case "creating":
        return [footer(inert(button("create", LINK_WORDS.creating, () => undefined, "")))];
      case "create-failed":
        return [note(state.message), footer(button("retry", LINK_WORDS.tryAgain, () => void enable(which), ""))];
      case "active":
        return activeContent(which, state);
      // The old link is not shown while its fate is open, and Copy stays
      // in its place, unable to act, so the focus has somewhere to be.
      case "replacing":
        return [note(LINK_WORDS.replacing), footer(inert(button("copy", LINK_WORDS.copy, () => undefined, "")))];
      case "turning-off":
        return [note(LINK_WORDS.turningOff), footer(inert(button("copy", LINK_WORDS.copy, () => undefined, "")))];
      case "checking":
        return [note(LINK_WORDS.checking), footer(inert(button("copy", LINK_WORDS.copy, () => undefined, "")))];
      case "unknown":
        return [note(LINK_WORDS.unknown[which]), footer(undefined, button("check", LINK_WORDS.checkAgain, () => void check(which, state.after, state.from, ++serials[which])))];
    }
  }

  /**
   * The panel's last row, in every state (g75): Close, then any lesser
   * action, then the state's main one, as the Text tab's row is Close then
   * Share. A state with nothing to do still has Close. The status line sits
   * just above it, so what a press said is next to the press.
   */
  function footer(main?: HTMLElement, ...lesser: HTMLElement[]): HTMLElement {
    return actionRow([close, ...lesser], main, "share-actions");
  }

  function activeContent(which: ShareMode, state: Held): HTMLElement[] {
    const url = el("textarea", {
      class: "share-link-url",
      attrs: { id: "share-link-url", readonly: true, rows: 3, spellcheck: "false", "data-key": "url" },
    });
    // The property, never the attribute: an attribute is markup, and the link
    // must not be in the page's markup.
    url.value = state.url;
    urlField = url;
    const copy = button("copy", LINK_WORDS.copy, () => copyLink(which), "");
    const share = useShare ? button("share", LINK_WORDS.share, () => shareLink(which)) : undefined;
    const manage = el("details", {
      class: "more-details share-manage",
      children: [
        el("summary", {
          class: "more-details-summary",
          attrs: { "data-key": "manage" },
          children: [el("span", { class: "more-details-label", text: LINK_WORDS.manage }), icon("chevron")],
        }),
        el("div", {
          class: "more-details-body share-manage-body",
          children: [
            note(LINK_WORDS.manageNote[which]),
            actionRow([
              button("replace", LINK_WORDS.replace, () => askReplace(which)),
              button("turn-off", LINK_WORDS.turnOff, () => askTurnOff(which)),
            ]),
          ],
        }),
      ],
    });
    manage.open = manageOpen;
    manage.addEventListener("toggle", () => { manageOpen = manage.open; });
    return [
      field("share-link-url", LINK_WORDS.field[which], url),
      manage,
      footer(copy, ...(share ? [share] : [])),
    ];
  }

  // ---- reading -------------------------------------------------------------

  function failurePhase(error: unknown): Phase {
    if (!(error instanceof ApiError)) return "failed";
    if (error.status === 0) return "offline";
    if (error.status === 401) return "signed-out";
    if (error.status === 404) return store.list(listId) ? "unsynced" : "gone";
    return "failed";
  }

  function held(state: ShareState): ModeState {
    return state.active ? { kind: "active", url: state.url, generation: state.generation } : { kind: "inactive" };
  }

  /** Takes the server's word for each mode that has nothing on its way. */
  function apply(answer: ShareStatus): void {
    for (const each of MODES) {
      if (STABLE.has(states[each].kind)) states[each] = held(answer[each]);
    }
  }

  function load(): void {
    if (!alive || phase === "gone") return;
    const serial = ++loadSerial;
    phase = "loading";
    render();
    // A device that has never heard the account's name asks the server
    // once, so an owner who named themselves elsewhere is not asked again.
    // A name held here, even an old one, is a name. Create waits for the
    // answer, so it never asks for a name the server was about to give; and
    // the answer is taken only while there is still no name here, so an
    // older answer cannot undo one saved since.
    const lookup = named(shell.user.name) ? Promise.resolve() : currentUser().then((user) => {
      if (alive && user && user.id === shell.user.id && !named(shell.user.name)) shell.updateUser(user);
    }, () => undefined);
    Promise.all([shareStatus(listId), lookup]).then(([answer]) => {
      if (!alive || serial !== loadSerial || phase === "gone") return;
      if (!validStatus(answer)) throw new ApiError(500, "unknown", LINK_WORDS.failed);
      apply(answer);
      phase = "ready";
      render();
    }).catch((error: unknown) => {
      if (!alive || serial !== loadSerial || phase === "gone") return;
      phase = failurePhase(error);
      render();
    });
  }

  // ---- creating ------------------------------------------------------------

  function create(which: ShareMode): void {
    if (phase !== "ready" || states[which].kind !== "inactive") return;
    if (named(shell.user.name)) {
      void enable(which);
      return;
    }
    askName(which);
  }

  /**
   * The owner's name, before the first link: the step keeps the dialog, the
   * chosen mode and what was asked for. Cancel creates nothing.
   */
  function askName(which: ShareMode): void {
    states[which] = { kind: "naming" };
    render();
    const finish = (user: Parameters<Shell["updateUser"]>[0] | undefined) => {
      if (naming !== form) return;
      naming = undefined;
      if (!alive) return;
      if (user) shell.updateUser(user);
      popup().resume();
      if (user) {
        void enable(which);
        return;
      }
      states[which] = { kind: "inactive" };
      render("create");
    };
    const form = accountNameForm({
      id: "share-owner-name",
      initial: "",
      explain: ACCOUNT_NAME.linkExplain,
      submit: ACCOUNT_NAME.continue,
      announce: shell.announce,
      saved: (user) => finish(user),
      cancel: () => finish(undefined),
    });
    naming = form;
    popup().step({
      title: ACCOUNT_NAME.linkTitle,
      body: form.node,
      focus: form.field,
      // Escape, Back and the backdrop answer Cancel, except while the name
      // is on its way: then the name would be saved and the link not made.
      cancel: () => { if (!form.busy()) finish(undefined); },
    });
  }

  async function enable(which: ShareMode): Promise<void> {
    if (phase !== "ready") return;
    const kind = states[which].kind;
    if (kind !== "inactive" && kind !== "naming" && kind !== "create-failed") return;
    const serial = ++serials[which];
    states[which] = { kind: "creating" };
    if (which === mode()) render("create");
    let answer: ShareState;
    try {
      answer = await enableShare(listId, which);
    } catch (error: unknown) {
      if (!alive || serial !== serials[which]) return;
      const failed = failurePhase(error);
      if (failed === "gone" || failed === "unsynced" || failed === "signed-out") {
        states[which] = { kind: "inactive" };
        phase = failed;
        // Signed out offers Sign in again, not Try again: named, or the focus
        // falls to the row's first control, which is Close (g75).
        render(failed === "signed-out" ? "sign-in" : "retry");
        return;
      }
      // Creating is safe to repeat: a mode already on answers with its link.
      const message = failed === "offline" ? LINK_WORDS.offline : `${LINK_WORDS.createFailed} ${messageOf(error)}`;
      states[which] = { kind: "create-failed", message };
      if (which === mode()) render("retry");
      say(message);
      return;
    }
    changed();
    if (!alive || serial !== serials[which] || phase !== "ready") return;
    if (!validState(answer) || !answer.active) {
      states[which] = { kind: "create-failed", message: LINK_WORDS.createFailed };
      if (which === mode()) render("retry");
      say(LINK_WORDS.createFailed);
      return;
    }
    states[which] = held(answer);
    if (which === mode()) render("copy");
    say(LINK_WORDS.ready[which]);
  }

  // ---- copying and sharing -------------------------------------------------

  /**
   * Selects the link for copying by hand, but only the one that was asked
   * for: after a change of mode or a replace, the field shows another.
   */
  function manual(which: ShareMode, url: string): void {
    const state = states[which];
    if (!urlField || which !== mode() || state.kind !== "active" || state.url !== url) {
      say(LINK_WORDS.copyFailed);
      return;
    }
    urlField.focus();
    urlField.select();
    say(LINK_WORDS.manual);
  }

  /** Called inside the click, with nothing awaited first: the clipboard wants the click's activation. */
  function copyLink(which: ShareMode): void {
    const state = states[which];
    if (state.kind !== "active" || copying) return;
    if (typeof navigator.clipboard?.writeText !== "function") {
      manual(which, state.url);
      return;
    }
    copying = true;
    const url = state.url;
    navigator.clipboard.writeText(url).then(
      () => { if (alive) say(LINK_WORDS.copied[which]); },
      () => { if (alive) manual(which, url); },
    ).finally(() => { copying = false; });
  }

  /** The same rule as Copy: the share sheet opens only from the click itself. */
  function shareLink(which: ShareMode): void {
    const state = states[which];
    if (state.kind !== "active" || copying || typeof navigator.share !== "function") return;
    copying = true;
    navigator.share({ url: state.url }).then(
      () => { if (alive) say(LINK_WORDS.shared); },
      (error: unknown) => {
        if (!alive) return;
        // The person closed the sheet: nothing to say.
        if (typeof error === "object" && error !== null && "name" in error && error.name === "AbortError") return;
        useShare = false;
        render("copy");
        say(LINK_WORDS.shareUnavailable);
      },
    ).finally(() => { copying = false; });
  }

  // ---- replacing and turning off --------------------------------------------

  function askReplace(which: ShareMode): void {
    const state = states[which];
    if (state.kind !== "active") return;
    const generation = state.generation;
    popup().confirm({
      title: LINK_WORDS.confirmReplace.title[which],
      message: LINK_WORDS.confirmReplace.message[which],
      keep: { label: LINK_WORDS.confirmReplace.keep, onChoose: () => { popup().resume(); render("replace"); } },
      act: { label: LINK_WORDS.replace, danger: true, onChoose: () => { popup().resume(); void replace(which, generation); } },
    });
  }

  function askTurnOff(which: ShareMode): void {
    const state = states[which];
    if (state.kind !== "active") return;
    const generation = state.generation;
    popup().confirm({
      title: LINK_WORDS.confirmOff.title[which],
      message: LINK_WORDS.confirmOff.message[which],
      keep: { label: LINK_WORDS.confirmOff.keep, onChoose: () => { popup().resume(); render("turn-off"); } },
      act: { label: LINK_WORDS.turnOff, danger: true, onChoose: () => { popup().resume(); void turnOff(which, generation); } },
    });
  }

  async function replace(which: ShareMode, generation: string): Promise<void> {
    const state = states[which];
    if (!alive || phase !== "ready" || state.kind !== "active" || state.generation !== generation) return;
    const serial = ++serials[which];
    states[which] = { kind: "replacing", url: state.url, generation };
    manageOpen = false;
    render("copy");
    let answer: ShareState;
    try {
      answer = await regenerateShare(listId, which, generation);
    } catch (error: unknown) {
      if (!alive || serial !== serials[which]) return;
      settleFailure(which, error, "replace", generation, serial);
      return;
    }
    changed();
    if (!alive || serial !== serials[which]) return;
    if (!validState(answer) || !answer.active) {
      void check(which, "replace", generation, serial);
      return;
    }
    states[which] = held(answer);
    if (which === mode()) render("copy");
    say(LINK_WORDS.replaced[which]);
  }

  async function turnOff(which: ShareMode, generation: string): Promise<void> {
    const state = states[which];
    if (!alive || phase !== "ready" || state.kind !== "active" || state.generation !== generation) return;
    const serial = ++serials[which];
    states[which] = { kind: "turning-off", url: state.url, generation };
    manageOpen = false;
    render("copy");
    try {
      await disableShare(listId, which);
    } catch (error: unknown) {
      if (!alive || serial !== serials[which]) return;
      settleFailure(which, error, "off", generation, serial);
      return;
    }
    changed();
    if (!alive || serial !== serials[which]) return;
    states[which] = { kind: "inactive" };
    if (which === mode()) render("create");
    say(LINK_WORDS.turnedOff[which]);
  }

  /**
   * A change that failed, or whose answer never came. A 401 changed nothing,
   * and a 404 says the list is not the owner's any more; anything else may
   * have happened on the server, so the status is read again.
   */
  function settleFailure(which: ShareMode, error: unknown, after: "replace" | "off", from: string, serial: number): void {
    const failed = failurePhase(error);
    if (failed === "gone" || failed === "unsynced" || failed === "signed-out") {
      states[which] = { kind: "unknown", after, from };
      phase = failed;
      render(failed === "signed-out" ? "sign-in" : "retry");
      return;
    }
    void check(which, after, from, serial);
  }

  async function check(which: ShareMode, after: "replace" | "off", from: string, serial: number): Promise<void> {
    if (!alive || serial !== serials[which]) return;
    states[which] = { kind: "checking", after, from };
    if (which === mode()) render("copy");
    let answer: ShareStatus;
    try {
      answer = await shareStatus(listId);
      if (!validStatus(answer)) throw new ApiError(500, "unknown", LINK_WORDS.failed);
    } catch {
      if (!alive || serial !== serials[which]) return;
      states[which] = { kind: "unknown", after, from };
      if (which === mode()) render("check");
      say(LINK_WORDS.unknown[which]);
      return;
    }
    changed();
    if (!alive || serial !== serials[which]) return;
    const now = answer[which];
    states[which] = held(now);
    const otherMode = other(which);
    if (STABLE.has(states[otherMode].kind)) states[otherMode] = held(answer[otherMode]);
    let message: string;
    if (after === "replace") message = now.active && now.generation !== from ? LINK_WORDS.replaced[which] : now.active ? LINK_WORDS.notReplaced[which] : LINK_WORDS.turnedOff[which];
    // A newer link from another tab is not the one that was turned off.
    else message = !now.active ? LINK_WORDS.turnedOff[which] : now.generation === from ? LINK_WORDS.notOff[which] : LINK_WORDS.offNewer[which];
    if (which === mode()) render(now.active ? "copy" : "create");
    say(message);
  }

  // ---- the list going ------------------------------------------------------

  const stopLists = store.onLists((change) => {
    if (change.id !== listId || change.list) return;
    phase = "gone";
    for (const each of MODES) {
      serials[each] += 1;
      states[each] = { kind: "inactive" };
    }
    manageOpen = false;
    render();
  });

  const online = () => { if (phase === "offline") load(); };
  window.addEventListener("online", online);

  render();

  return {
    node,
    show() {
      if (phase === "idle") load();
    },
    busy: () => naming?.busy() ?? false,
    dispose() {
      alive = false;
      urlField = undefined;
      stopLists();
      window.removeEventListener("online", online);
    },
  };
}
